import React from 'react';

function SectionHeading({ title, subtitle }) {
  return (
    <div className="text-center mb-12">
      <h2 className="text-3xl font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="mt-2 text-gray-600 max-w-3xl mx-auto">{subtitle}</p>}
    </div>
  );
}

function InvestmentsHealthcare() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/medicine-8287535_640.jpg" alt="Healthcare" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Healthcare</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">MedTech, life sciences, diagnostics and healthcare IT investments.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading title="Investment Thesis" subtitle="Blockfirst Capital targets transformative healthcare innovations driven by demographic shifts, technological breakthroughs, and evolving care delivery models." />
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Why Healthcare Now</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The global healthcare sector is undergoing its most significant transformation in decades. An aging population, rising chronic disease burden, and healthcare cost pressures are creating unprecedented demand for innovative solutions. We invest in companies that improve patient outcomes while reducing system costs through technology, data analytics, and novel care delivery models.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our investment strategy focuses on three core pillars: clinical efficacy, economic value, and scalability. We seek companies with strong intellectual property, regulatory pathways, and clear reimbursement strategies. Our team includes former healthcare executives, clinicians, and regulatory experts who provide operational support post-investment.
              </p>
              <div className="bg-blue-50 border-l-4 border-[#0056d7] p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-[#0056d7]">Market Opportunity:</strong> The global healthcare market exceeds $12 trillion annually, with digital health, precision medicine, and value-based care representing the fastest-growing segments at 15-25% CAGR.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/surgery-1807541_640.jpg" alt="Healthcare thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -left-5 w-40 h-40 bg-[#0056d7] opacity-10 rounded-3xl" />
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              {title: 'MedTech Innovation', desc: 'Next-gen medical devices, robotics, imaging systems, and minimally invasive surgical tools improving patient outcomes.'},
              {title: 'Digital Health', desc: 'Telehealth platforms, remote patient monitoring, AI diagnostics, and digital therapeutics transforming care delivery.'},
              {title: 'Biopharma Tools', desc: 'Life sciences tools, CRISPR technologies, genomics platforms, and bioprocessing equipment accelerating drug discovery.'},
              {title: 'Healthcare IT', desc: 'EHR systems, revenue cycle management, care coordination platforms, and interoperability solutions.'}
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sub‑sectors grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading title="Focus Areas" />
          <div className="grid md:grid-cols-3 gap-6">
            {[{t:'MedTech',d:'Implantables, robotics, imaging and minimally invasive technologies.',img:'/images/medicine-8287535_640.jpg'},{t:'Life Sciences',d:'Tools, diagnostics, genomics and bioprocess innovation.',img:'/images/surgery-1807541_640.jpg'},{t:'Healthcare IT',d:'Data platforms, revenue cycle, scheduling and telehealth.',img:'/images/treatment-4099432_640.jpg'}].map(x=> (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm">
                <img src={x.img} alt={x.t} className="w-full h-40 object-cover" />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading title="Our Investment Approach" subtitle="Rigorous due diligence combining clinical expertise, commercial assessment, and regulatory analysis." />
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Due Diligence Framework</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-[#0056d7] pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Clinical & Scientific Review</h4>
                  <p className="text-sm text-gray-600">Our clinical advisory board evaluates efficacy data, safety profiles, mechanism of action, and competitive positioning. We assess clinical trial design, endpoints, and statistical rigor.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Regulatory Strategy</h4>
                  <p className="text-sm text-gray-600">We map FDA/EMA pathways, identify 510(k) vs. PMA requirements, assess breakthrough device designations, and model approval timelines with contingency planning.</p>
                </div>
                <div className="border-l-4 border-[#0056d7] pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Reimbursement & Market Access</h4>
                  <p className="text-sm text-gray-600">We analyze CPT codes, CMS coverage decisions, payer policies, health economics (ICER, QALY), and budget impact models to ensure commercial viability.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">IP & Competitive Landscape</h4>
                  <p className="text-sm text-gray-600">Patent portfolio review, freedom-to-operate analysis, competitive moat assessment, and trade secret protection strategies.</p>
                </div>
              </div>
            </div>
            <div>
              <img src="/new-assets/pexels-tima-miroshnichenko-5439443 (1).jpg" alt="Investment Process" className="w-full h-80 object-cover rounded-2xl shadow-xl mb-6" />
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3">Post-Investment Value Creation</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex gap-2"><span className="text-[#0056d7]">✓</span> Clinical development strategy & trial design optimization</li>
                  <li className="flex gap-2"><span className="text-[#0056d7]">✓</span> Regulatory affairs support & FDA/EMA liaison</li>
                  <li className="flex gap-2"><span className="text-[#0056d7]">✓</span> Commercial team building & go-to-market strategy</li>
                  <li className="flex gap-2"><span className="text-[#0056d7]">✓</span> Strategic partnerships & business development</li>
                  <li className="flex gap-2"><span className="text-[#0056d7]">✓</span> Operational excellence & quality systems</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Measurable Impact</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our healthcare portfolio companies deliver quantifiable improvements in patient outcomes and healthcare economics.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '30%', label: 'Average reduction in hospital readmissions'},
              {metric: '2.5x', label: 'Improvement in diagnostic accuracy'},
              {metric: '$1.2B', label: 'Annual healthcare cost savings'},
              {metric: '50M+', label: 'Patients impacted globally'}
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-bold text-[#fbbf24] mb-2">{item.metric}</div>
                <div className="text-sm text-white/70">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold">Discuss Healthcare Opportunities</h3>
          <p className="mt-2 text-white/80">Request our latest pipeline, due‑diligence checklist, and case studies.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border border-white/30">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsHealthcare;


